import * as React from 'react'

import { dayjs } from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import { CalendarEvent } from '@/features/calendar/types'

import { EventComponent } from './event-component.tsx'

type MonthViewProps = {
  currentDate: Date
  events?: CalendarEvent[]
}

const weekdaysShort = Array.from({ length: 7 }).map((_, i) =>
  dayjs()
    .day(i + 1)
    .format('ddd')
)

export const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
}) => {
  const monthStart = dayjs(currentDate).startOf('month')
  const monthEnd = dayjs(currentDate).endOf('month')
  const startDate = monthStart.startOf('week')
  const endDate = monthEnd.endOf('week')

  const days = []
  let day = startDate
  while (day.isBefore(endDate) || day.isSame(endDate, 'day')) {
    days.push(day)
    day = day.add(1, 'day')
  }

  return (
    <div className="bg-muted grid grid-cols-7 gap-px overflow-hidden rounded-lg border">
      {weekdaysShort.map((weekday) => (
        <div
          key={weekday}
          className="bg-background p-2 text-center text-sm font-medium first-letter:uppercase"
        >
          {weekday}
        </div>
      ))}

      {days.map((day) => {
        const dateEvents =
          events?.filter(
            (event) =>
              day.isSameOrAfter(dayjs.tz(event.start_date)) &&
              day.isSameOrBefore(dayjs.tz(event.end_date))
          ) ?? []
        const isCurrentMonth = day.month() === monthStart.month()

        return (
          <div
            key={day.toString()}
            className={cn(
              'bg-background relative min-h-[180px] p-2',
              !isCurrentMonth && 'text-muted-foreground'
            )}
          >
            <div className="mb-1 flex items-center justify-center">
              <span
                className={cn(
                  'grid size-8 place-items-center rounded-full',
                  day.isSame(dayjs(), 'day') &&
                    'bg-primary text-primary-foreground'
                )}
              >
                {day.format('D')}
              </span>
            </div>
            <div className="space-y-1">
              {dateEvents.slice(0, 3).map((event, index) => (
                <EventComponent key={index} event={event}>
                  <div
                    className={cn(
                      'bg-secondary line-clamp-2 cursor-pointer rounded p-1 text-xs'
                    )}
                  >
                    {event.title}
                  </div>
                </EventComponent>
              ))}
              {dateEvents.length > 3 && (
                <div className="text-muted-foreground text-xs">
                  +{dateEvents.length - 3} more
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
